import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HERO_DEMO as D } from '../content/narrative';
import { CadDrawing, FeaDrawing, RigRender, CorrelationChart, HERO_IMAGES } from './HeroDrawings';

/* The hero's live illustration: the whole loop, once around, then Rev B.

   Describe types the goal into the new sub-assembly's field (a project
   field, never a chat window). Require fills MIL's plan. From there each step
   shows its drawing, MIL's one-line result, and then Approve presses itself —
   "with you approving each step" made literal — and a record drops into
   "Kept for Rev B". After Learn it holds on "Rev B starts with 5 records."
   and loops. Step names are buttons: clicking one stops the loop and shows
   that step; Replay restarts it. Reduced motion opens on the finished record
   with no autoplay. Height is reserved so the hero never shifts. */

const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
const GREEN = 'var(--wb-green)';
const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const FULL = D.goal.length;
const LAST = D.steps.length - 1;
const START = { step: 0, typed: 0, plan: 0, pressed: false, approved: false, records: 0, finale: false };
/* The settled view of step k: everything up to and including its approval. */
const settled = (k) => ({ step: k, typed: FULL, plan: D.plan.length, pressed: false, approved: k >= 1, records: Math.max(0, k), finale: k === LAST });

/* One run of the loop as a list of [time, patch] events. */
const timeline = () => {
  const ev = []; let t = 600;
  for (let c = 1; c <= FULL; c += 1) ev.push([t + c * 24, { typed: c }]);
  t += FULL * 24 + 500;
  ev.push([t, { step: 1 }]);
  D.plan.forEach((_, k) => ev.push([t + 300 + k * 330, { plan: k + 1 }]));
  t += 300 + D.plan.length * 330 + 700;
  ev.push([t, { pressed: true }], [t + 350, { pressed: false, approved: true, records: 1 }]);
  t += 350 + 1000;
  for (let k = 2; k <= LAST; k += 1) {
    ev.push([t, { step: k, approved: false }]);
    t += 1700;
    ev.push([t, { pressed: true }], [t + 350, { pressed: false, approved: true, records: k }]);
    t += 350 + 1000;
  }
  ev.push([t - 700, { finale: true }]);
  return { ev, length: t + 4300 };
};

const Frame = ({ step, s }) => {
  switch (D.steps[step].key) {
    case 'describe':
      return (
        <div className="flex flex-col justify-center" style={{ height: '100%', padding: '0 18px' }}>
          <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>{D.parent} · {D.prompt}</div>
          <div style={{ border: '1px solid var(--wb-ink)', borderRadius: 10, padding: '10px 12px', fontFamily: sans, fontSize: 'var(--t-md)', lineHeight: 1.5, color: 'var(--wb-ink)', minHeight: 70, background: 'var(--wb-input)' }}>
            {D.goal.slice(0, s.typed)}{s.typed < FULL && <span className="hero-caret" aria-hidden />}
          </div>
        </div>
      );
    case 'require':
      return (
        <div className="flex flex-col justify-center" style={{ height: '100%', padding: '0 18px' }}>
          <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 700, marginBottom: 8 }}>MIL’s plan · proposed</div>
          <ul className="list-none p-0 m-0" style={{ display: 'grid', gap: 5 }}>
            {D.plan.map((item, k) => {
              const next = item.state === 'next';
              return (
                <li key={item.text} className="flex items-center justify-between gap-3" style={{ padding: '6px 10px', borderRadius: 8, border: `1px dashed ${next ? 'var(--wb-line)' : 'var(--wb-orange)'}`, background: next ? 'transparent' : 'rgba(185,79,30,0.04)', opacity: k < s.plan ? 1 : 0, transition: 'opacity .3s' }}>
                  <span style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: next ? 'var(--wb-muted)' : 'var(--wb-ink)' }}>{next ? '○' : '◆'} {item.text}</span>
                  <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: next ? 'var(--wb-muted)' : 'var(--wb-orange)' }}>{item.state}</span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    case 'design': return <CadDrawing />;
    case 'analyze': return <FeaDrawing />;
    case 'test': return <RigRender />;
    default: return <CorrelationChart />;
  }
};

const HeroDemo = () => {
  const [s, setS] = useState(() => (reduced() ? settled(LAST) : START));
  const [auto, setAuto] = useState(() => !reduced());
  const [run, setRun] = useState(0);
  const timers = useRef([]);

  const stop = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  /* Warm the three images so each step's frame is ready when it arrives. */
  useEffect(() => { HERO_IMAGES.forEach((src) => { const im = new Image(); im.src = src; }); }, []);

  useEffect(() => {
    if (!auto) return undefined;
    const { ev, length } = timeline();
    setS(START);
    timers.current = ev.map(([t, patch]) => setTimeout(() => setS((cur) => ({ ...cur, ...patch })), t));
    timers.current.push(setTimeout(() => setRun((r) => r + 1), length));
    return stop;
  }, [auto, run]);

  const jump = useCallback((k) => { stop(); setAuto(false); setS(settled(k)); }, []);
  const replay = () => { setAuto(true); setRun((r) => r + 1); };

  const step = D.steps[s.step];
  const btnState = s.approved ? 'done' : s.pressed ? 'pressed' : 'idle';

  return (
    <div
      aria-label="Example: a drive module walked through the loop — each step MIL proposes, you approve, and a record is kept for Rev B"
      style={{ background: 'var(--wb-paper)', border: '1px solid var(--wb-line)', borderRadius: 16, boxShadow: '0 12px 28px var(--wb-shadow)', overflow: 'hidden', textAlign: 'left' }}
    >
      {/* the loop, live — each name jumps to that step */}
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1" style={{ padding: '8px 12px', background: 'var(--wb-soft)', borderBottom: '1px solid var(--wb-line)' }}>
        {D.loop.map((name, i) => {
          const cur = i === s.step && !s.finale;
          return (
            <React.Fragment key={name}>
              {i > 0 && <span aria-hidden style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-line)' }}>→</span>}
              <button
                type="button"
                onClick={() => jump(i)}
                aria-current={cur ? 'step' : undefined}
                style={{
                  fontFamily: mono, fontSize: 'var(--t-sm)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 6, border: 0, cursor: 'pointer',
                  color: cur ? 'var(--wb-on-ink)' : i < s.step || s.finale ? 'var(--wb-ink)' : 'var(--wb-muted)',
                  background: cur ? 'var(--wb-orange)' : 'transparent', fontWeight: i <= s.step ? 700 : 400, transition: 'background .3s, color .3s',
                }}
              >
                {name}
              </button>
            </React.Fragment>
          );
        })}
        <span aria-hidden title="The loop returns to Describe for Rev B" style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: s.finale ? 'var(--wb-on-ink)' : 'var(--wb-orange)', background: s.finale ? 'var(--wb-orange)' : 'transparent', borderRadius: 6, padding: '2px 6px', transition: 'background .3s' }}>↺</span>
        {!auto && !reduced() && (
          <button type="button" onClick={replay} style={{ marginLeft: 'auto', fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', background: 'transparent', border: 0, cursor: 'pointer' }}>▶ Replay</button>
        )}
      </div>

      {/* the step's visual */}
      <div style={{ aspectRatio: '480 / 250', background: 'var(--wb-frame)', borderBottom: '1px solid var(--wb-line)' }}>
        <div className="hero-frame" key={s.step} style={{ height: '100%' }}><Frame step={s.step} s={s} /></div>
      </div>

      {/* MIL's result and the approval */}
      {/* Stacked on phones with a reserved height, so MIL's line can wrap
          without the card changing height from step to step. */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 min-h-[132px] sm:min-h-[64px]" style={{ padding: '10px 14px', borderBottom: '1px solid var(--wb-line)' }}>
        <p style={{ margin: 0, fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', lineHeight: 1.45, opacity: step.mil ? 1 : 0.5 }}>
          <span style={{ fontFamily: mono, color: 'var(--wb-orange)', fontWeight: 800, marginRight: 6 }}>MIL</span>
          {step.mil || D.working}
        </p>
        {step.action && (
          <span
            aria-hidden
            className={btnState === 'pressed' ? 'hero-press' : undefined}
            style={{
              flex: '0 0 auto', whiteSpace: 'nowrap', fontFamily: sans, fontSize: 'var(--t-sm)', fontWeight: 700, padding: '7px 14px', borderRadius: 999,
              border: `1px solid ${btnState === 'done' ? GREEN : 'var(--wb-ink)'}`,
              background: btnState === 'done' ? 'transparent' : btnState === 'pressed' ? 'var(--wb-ink-hover)' : 'var(--wb-ink)',
              color: btnState === 'done' ? GREEN : 'var(--wb-on-ink)', transform: btnState === 'pressed' ? 'scale(0.94)' : 'none', transition: 'transform .15s, background .2s',
            }}
          >
            {btnState === 'done' ? `✓ ${step.done}` : step.action}
          </span>
        )}
      </div>

      {/* the record, building */}
      <div style={{ padding: '10px 14px 12px' }}>
        <div className="flex items-baseline justify-between gap-2">
          <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{D.recordsLabel}</span>
          <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)' }}>{s.records} / {LAST}</span>
        </div>
        <ol className="list-none p-0 m-0" style={{ marginTop: 6, display: 'grid', gap: 3 }}>
          {D.steps.slice(1).map((st, k) => {
            const on = k < s.records;
            return (
              <li key={st.key} className="flex items-baseline gap-2" style={{ fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', minHeight: 20, opacity: on ? 1 : 0, transform: on ? 'none' : 'translateY(-4px)', transition: 'opacity .35s, transform .35s' }}>
                <span style={{ fontFamily: mono, fontWeight: 800, whiteSpace: 'nowrap' }}>{st.record[0]}</span>
                <span style={{ color: 'var(--wb-muted)' }}>{st.record[1]}</span>
              </li>
            );
          })}
        </ol>
        <p style={{ margin: '6px 0 0', fontFamily: sans, fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--wb-orange)', minHeight: 20, opacity: s.finale ? 1 : 0, transition: 'opacity .4s' }}>{D.finale}</p>
      </div>

      {/* the full sequence for screen readers and crawlers */}
      <div className="sr-only">
        <p>{D.parent}: {D.goal}</p>
        {D.steps.slice(1).map((st) => <p key={st.key}>{D.loop[D.steps.indexOf(st)]}: {st.mil} {st.done}. Kept: {st.record.join(', ')}.</p>)}
        <p>{D.finale}</p>
      </div>
    </div>
  );
};

export default HeroDemo;
